import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { GuiasRelacionadas } from "@/components/GuiasRelacionadas";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";
import hero640Asset from "../assets/hero-640.webp.asset.json";

const srcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;
const OG_IMAGE = `https://elpajardebenito.es${hero1280Asset.url}`;
const URL_CANON = "https://elpajardebenito.es/casa-rural-guadalajara";

const faqs = [
  { q: "¿Dónde está exactamente la casa rural?", a: "En El Ordial, un pequeño pueblo de la Sierra del Alto Rey, en la provincia de Guadalajara (Castilla-La Mancha), a unos 45 minutos de la capital provincial y a 1 h 30 min de Madrid." },
  { q: "¿Cuántas personas caben?", a: "Hasta 14 personas. Se puede alquilar la casa rural completa o reservar habitaciones dobles con baño privado de forma independiente." },
  { q: "¿Qué precio tiene una casa rural en Guadalajara?", a: "En El Pajar de Benito los precios por habitación arrancan desde 55 € por noche en uso individual y 70 € la habitación doble, según temporada. Reservando directamente en la web no pagas comisiones de intermediarios." },
  { q: "¿Se admiten mascotas?", a: "Consúltanos antes de reservar: valoramos cada caso según el tamaño del animal y la ocupación de la casa." },
  { q: "¿Qué hay que ver en la provincia de Guadalajara?", a: "El Hayedo de Tejera Negra, los pueblos negros de la arquitectura negra (Majaelrayo, Valverde de los Arroyos, Campillo de Ranas), el Pico Alto Rey, Sigüenza y el embalse de Beleña." },
];

export const Route = createFileRoute("/casa-rural-guadalajara")({
  head: () => ({
    meta: [
      { title: "Casa rural en Guadalajara desde 55 € · Habitaciones o casa entera" },
      { name: "description", content: "Casa rural en Guadalajara: 6 habitaciones con baño privado desde 55 €/noche o la casa completa para 14 personas, con chimenea y jardín en la Sierra Norte. Reserva directa sin comisiones." },
      { property: "og:title", content: "Casa rural en Guadalajara desde 55 €" },
      { property: "og:description", content: "Habitaciones con baño privado o casa completa para 14 personas en la Sierra Norte de Guadalajara. Chimenea, jardín y reserva directa." },
      { property: "og:url", content: URL_CANON },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL_CANON }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Casa rural en Guadalajara", to: "/casa-rural-guadalajara" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Casa rural en Guadalajara" }]} />
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={hero1280Asset.url} srcSet={srcSet} sizes="100vw" alt="Casa rural en Guadalajara rodeada de naturaleza en la Sierra del Alto Rey" width={1920} height={1280} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-4 pb-16 md:px-6">
          <p className="text-xs uppercase tracking-[0.28em] text-white/80">Sierra del Alto Rey</p>
          <h1 className="mt-3 font-serif text-4xl text-white md:text-5xl">Casa rural en Guadalajara</h1>
          <p className="mt-4 max-w-2xl text-white/90">De 2 a 14 personas en El Ordial. Chimenea, jardín privado y silencio de verdad, en plena Sierra Norte de Guadalajara.</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Una casa rural en Guadalajara con encanto</h2>
        <p className="mt-4 text-muted-foreground">
          <strong>El Pajar de Benito</strong> es una <strong>casa rural en Guadalajara</strong> rehabilitada respetando la arquitectura tradicional
          serrana: piedra, madera y grandes ventanales hacia la montaña. Está en El Ordial, en la falda del Pico Alto Rey, dentro de la
          <strong> Sierra Norte de Guadalajara</strong>, la zona de mayor valor natural de la provincia.
        </p>
        <p className="mt-4 text-muted-foreground">
          Puedes alquilarla <strong>completa para grupos</strong> (hasta 14 plazas) o reservar solo una <strong>habitación doble con baño privado</strong>
          si viajáis en pareja. En ambos casos reservas directamente con nosotros, sin comisiones de portales intermediarios.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { t: "Hasta 14 plazas", d: "Casa completa para grupos o habitaciones dobles independientes." },
            { t: "Chimenea de leña", d: "Salón con chimenea y calefacción central para el invierno." },
            { t: "Jardín privado", d: "Zona exterior con vistas abiertas a la sierra." },
            { t: "Naturaleza", d: "Alto Rey, Hayedo de Tejera Negra y pueblos negros a un paso." },
            { t: "Cielos oscuros", d: "Baja contaminación lumínica: Vía Láctea a simple vista." },
            { t: "Reserva directa", d: "Mejor precio garantizado y trato personal." },
          ].map((b) => (
            <div key={b.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg">{b.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-muted/40">
        <div className="mx-auto max-w-4xl px-4 py-16 md:px-6">
          <h2 className="font-serif text-3xl text-foreground">Qué ver cerca en la provincia de Guadalajara</h2>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li><strong>Pico Alto Rey (1.858 m):</strong> ascensión señalizada desde el propio pueblo, con vistas a toda la sierra.</li>
            <li><strong>Pueblos negros:</strong> Majaelrayo, Valverde de los Arroyos y Campillo de Ranas, la famosa arquitectura negra de pizarra.</li>
            <li><strong>Hayedo de Tejera Negra:</strong> uno de los hayedos más meridionales de Europa, espectacular en otoño.</li>
            <li><strong>Sigüenza:</strong> ciudad medieval con catedral y castillo, a menos de una hora.</li>
            <li><strong>Embalse de Beleña y Hiendelaencina:</strong> baño en verano y patrimonio minero de la plata.</li>
          </ul>
          <div className="mt-8">
            <Link to="/entorno" className="text-sm font-medium text-primary hover:underline">Ver toda la guía del entorno →</Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Preguntas frecuentes</h2>
        <div className="mt-8 space-y-6">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-foreground">{f.q}</h3>
              <p className="mt-2 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <GuiasRelacionadas slugs={["que-ver-sierra-norte-guadalajara", "arquitectura-negra", "subida-alto-rey"]} />

      <section className="mx-auto max-w-3xl px-4 pb-24 text-center md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">Consulta fechas y precios</h2>
        <p className="mt-4 text-muted-foreground">Disponibilidad en tiempo real y reserva directa sin comisiones.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/reserva" className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Ver disponibilidad</Link>
          <Link to="/alojamiento" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-muted">Ver habitaciones</Link>
        </div>
      </section>
    </>
  );
}
